import {EventPayload, AssetData, EventStatus} from '../../../state/rest'
import {useAppState} from '../../../state'
import {useState, useCallback, useReducer, useEffect, useRef} from 'react'
import {
  reverseConvertUnit,
  localNumberFormat,
  deLocalizeValue,
  valueExists,
  reverseConvertWeight
} from '../../../config/utils'
import {WasteTaskType} from '../../../Components/Views/Tasks/CreateWasteTask'
import {MeasurementMethodOption} from '../../../state/home/site/state'

const deriveOpenedTaskPayload = (initialPayload: EventPayload): Partial<EventPayload> => {
  const isTempTask = ['cooling', 'temperature', 'equipmentTemperature', 'dishwasherTemperature'].includes(
    initialPayload.task.taskType?.id as string
  )
  const parseNum = (x: any) => (typeof x === 'number' ? x : typeof x === 'string' ? parseFloat(x) : NaN)
  const minV = parseNum((initialPayload.task as any)?.minValue)
  const maxV = parseNum((initialPayload.task as any)?.maxValue)
  const hasNegativeLimit =
    (typeof minV === 'number' && !isNaN(minV) && minV < 0) || (typeof maxV === 'number' && !isNaN(maxV) && maxV < 0)

  return {
    ...initialPayload,
    value: isTempTask && hasNegativeLimit ? '-' : undefined,
    recordingDay: new Date().toISOString(),
    reportedRecipient: undefined,
    reportedSupplier: undefined
  }
}

export function usePerformingTasks(initialPayload: EventPayload, eventId?: string, afterSubmit?: () => void) {
  const {state, actions} = useAppState()
  // use consistent representation for eventId so initial key matches lastKey
  const key = `${initialPayload.task.id}-${initialPayload.scheduledAt}-${eventId ?? ''}`

  const [payload, setPayload] = useReducer<React.Reducer<EventPayload, Partial<EventPayload>>>(
    (a, b) => ({...a, ...b}),
    {...initialPayload, ...deriveOpenedTaskPayload(initialPayload)}
  )
  const assetsRef = useRef<AssetData[]>(initialPayload.assets ?? [])

  useEffect(() => {
    assetsRef.current = payload.assets ?? []
  }, [payload.assets])
  const [loading, setLoading] = useState(false)
  // Used to force re-mount of form inputs after Save & Add New so internal component state resets
  const [resetTick, setResetTick] = useState(0)

  const [currentEventId, setCurrentEventId] = useState<string | undefined>(eventId)

  // 👇 key used only to detect when a NEW task/event is opened
  const [lastKey, setLastKey] = useState(`${initialPayload.task.id}-${initialPayload.scheduledAt}-${eventId ?? ''}`)

  useEffect(() => {
    // Only run when the "identity" of the opened task/event actually changes
    if (key === lastKey) return

    setLastKey(key)
    setCurrentEventId(eventId) // sync our internal id to the newly opened one

    setPayload(deriveOpenedTaskPayload(initialPayload))

    // if you want children to fully reset their internal state as well:
    setResetTick(tick => tick + 1)

    // We intentionally key off `key` (and read `initialPayload` from closure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPayload.task.id, initialPayload.scheduledAt, eventId, lastKey])

  const taskType = payload.task.taskType?.id
  const taskPayload = {
    id: payload.task.id,
    taskType: {
      id: payload.task.taskType?.id
    }
  }

  const isMeasurableEvent = (type: string) =>
    ['cooling', 'temperature', 'equipmentTemperature', 'dishwasherTemperature', 'hygiene'].includes(type)

  let value: number | undefined = undefined
  const signOnly = typeof payload.value === 'string' && /^[+-]$/.test(payload.value.trim())
  if (!signOnly) {
    if (taskType === 'cooling' || taskType === 'equipmentTemperature' || taskType === 'temperature') {
      value = reverseConvertUnit(state.site!, deLocalizeValue(state.site!.locale, payload.value))
    } else if (taskType === 'waste') {
      if (initialPayload.task.wasteTaskType !== WasteTaskType.CustomerAmount && valueExists(payload.value)) {
        value = reverseConvertWeight(state.site!, deLocalizeValue(state.site!.locale, payload.value))
      } else {
        value = deLocalizeValue(state.site!.locale, payload.value)
      }
    } else {
      value = deLocalizeValue(state.site!.locale, payload.value)
    }
  } else {
    value = undefined
  }

  const scheduledAtToSend = payload.scheduledAt || undefined
  const measurements =
    taskType === 'dishwasherTemperature'
      ? payload.measurements?.map(measurement => ({
          ...measurement,
          value: reverseConvertUnit(state.site!, deLocalizeValue(state.site!.locale, measurement.value))
        }))
      : undefined

  const handleRemove = async () => {
    setLoading(true)
    let wasSuccessful = false
    if (!!currentEventId) {
      const updatedEvent = await actions.v1.performTask.updateEvent({
        id: currentEventId,
        payload: {
          aromiProduct: payload.aromiProduct ?? payload.task.aromiProduct,
          suggestions: payload.suggestions,
          reportedActor: payload.reportedActor,
          remarks: payload.remarks,
          comment: payload.comment,
          value,
          measurements,
          recordingDay: payload.recordingDay,
          samplecollectionDate: payload.samplecollectionDate,
          doneSubtasks: payload.doneSubtasks,
          selectedSubtaskOptions: payload.selectedSubtaskOptions,
          subtaskComments: payload.subtaskComments,
          relatedSites: payload.relatedSites,
          scheduledAt: scheduledAtToSend,
          assets: payload.assets?.map(a => a.id) ?? [],
          status: EventStatus.REJECTED,
          task: taskPayload
        }
      })
      wasSuccessful = !!updatedEvent
    } else {
      const createdEvent = await actions.v1.performTask.createEvent({
        ...payload,
        aromiProduct: payload.aromiProduct ?? payload.task.aromiProduct,
        assets: payload.assets?.map(a => a.id) ?? [],
        samplecollectionDate: payload.samplecollectionDate,
        recordingDay: payload.recordingDay,
        scheduledAt: scheduledAtToSend,
        value,
        status: EventStatus.REJECTED,
        task: taskPayload
      })
      if (createdEvent?.id) {
        setCurrentEventId(createdEvent.id)
      }
      wasSuccessful = !!createdEvent
    }
    setLoading(false)
    if (wasSuccessful && afterSubmit) {
      afterSubmit()
    }
  }
  const handleSubmit = async () => {
    setLoading(true)

    await actions.v1.performTask.createEvent({
      ...payload,
      aromiProduct: payload.aromiProduct ?? payload.task.aromiProduct,
      assets: payload.assets?.map(a => a.id) ?? [],
      samplecollectionDate: payload.samplecollectionDate,
      recordingDay: payload.recordingDay,
      scheduledAt: scheduledAtToSend,
      value,
      measurements,
      measurementMethod: isMeasurableEvent(taskType) ? MeasurementMethodOption.MANUAL : undefined,
      task: taskPayload,
      reportedRecipient: payload.reportedRecipient ? payload.reportedRecipient[0] : '-',
      reportedSupplier: payload.reportedSupplier ? payload.reportedSupplier[0] : '-'
    })
    setLastKey('')
    setLoading(false)

    if (afterSubmit) {
      afterSubmit()
    }
  }

  const handleUpdate = async () => {
    if (!currentEventId) return
    setLoading(true)
    await actions.v1.performTask.updateEvent({
      id: currentEventId,
      payload: {
        aromiProduct: payload.aromiProduct ?? payload.task.aromiProduct,
        ingredients: payload.ingredients,
        suggestions: payload.suggestions,
        remarks: payload.remarks,
        comment: payload.comment,
        reportedActor: payload.reportedActor,
        value,
        measurements,
        recordingDay: payload.recordingDay,
        samplecollectionDate: payload.samplecollectionDate,
        doneSubtasks: payload.doneSubtasks,
        selectedSubtaskOptions: payload.selectedSubtaskOptions,
        subtaskComments: payload.subtaskComments,
        relatedSites: payload.relatedSites ?? [],
        scheduledAt: scheduledAtToSend,
        assets: payload.assets?.map(a => a.id) ?? [],
        task: taskPayload,
        measurementMethod: isMeasurableEvent(taskType) ? MeasurementMethodOption.MANUAL : undefined,
        incompleteToDo: payload.incompleteToDo,
        reportedRecipient: payload.reportedRecipient ? payload.reportedRecipient[0] : '-',
        reportedSupplier: payload.reportedSupplier ? payload.reportedSupplier[0] : '-'
      }
    })
    setLoading(false)
    if (afterSubmit) {
      afterSubmit()
    }
  }

  const onAssetSaved = (asset?: AssetData) => {
    if (!asset) return

    const next = [...assetsRef.current, asset]
    const unique = Array.from(new Map(next.map(a => [a.id, a])).values())

    assetsRef.current = unique
    setPayload({assets: unique})
  }

  const onAssetRemoved = (assetId?: string) => {
    if (!assetId) return

    const next = assetsRef.current.filter(a => a.id !== assetId)

    assetsRef.current = next
    setPayload({assets: next})
  }

  const handleNumberBlur = (value: string) => {
    if (value.length) setValue('value', localNumberFormat(state.site!.locale, value))
  }
  const setValue = useCallback((key: keyof EventPayload, value: any) => {
    setPayload({[key]: value})
  }, [])

  const handleCancel = () => {
    // When user cancels, reset visible fields but set scheduledAt and recordingDay to today
    const isTempTask = ['cooling', 'temperature', 'equipmentTemperature'].includes(
      initialPayload.task.taskType?.id as string
    )
    const parseNum = (x: any) => (typeof x === 'number' ? x : typeof x === 'string' ? parseFloat(x) : NaN)
    const minV = parseNum((initialPayload.task as any)?.minValue)
    const maxV = parseNum((initialPayload.task as any)?.maxValue)
    const hasNegativeLimit =
      (typeof minV === 'number' && !isNaN(minV) && minV < 0) || (typeof maxV === 'number' && !isNaN(maxV) && maxV < 0)
    setPayload({
      ...initialPayload,
      value: isTempTask && hasNegativeLimit ? '-' : undefined,
      reportedActor: undefined,
      reportedRecipient: undefined,
      reportedSupplier: undefined,
      assets: undefined,
      remarks: undefined,
      suggestions: [],
      ingredients: [],
      choice: ''
    })
  }

  // Add missing handleSubmitAndAddNew function
  const handleSubmitAndAddNew = async () => {
    setLoading(true)
    // Save the current event
    if (currentEventId) {
      await actions.v1.performTask.updateEvent({
        id: currentEventId,
        payload: {
          ...payload,
          aromiProduct: payload.aromiProduct ?? payload.task.aromiProduct,
          suggestions: payload.suggestions,
          remarks: payload.remarks,
          comment: payload.comment,
          reportedActor: payload.reportedActor,
          value,
          measurements,
          doneSubtasks: payload.doneSubtasks,
          selectedSubtaskOptions: payload.selectedSubtaskOptions,
          subtaskComments: payload.subtaskComments,
          assets: payload.assets?.map(a => a.id) ?? [],
          scheduledAt: scheduledAtToSend,
          task: taskPayload,
          measurementMethod: isMeasurableEvent(taskType) ? MeasurementMethodOption.MANUAL : undefined,
          incompleteToDo: payload.incompleteToDo,
          recordingDay: payload.recordingDay
        }
      })
    } else {
      await actions.v1.performTask.createEvent({
        ...payload,
        aromiProduct: payload.aromiProduct ?? payload.task.aromiProduct,
        assets: payload.assets?.map(a => a.id) ?? [],
        samplecollectionDate: payload.samplecollectionDate,
        recordingDay: payload.recordingDay,
        scheduledAt: scheduledAtToSend,
        value,
        measurementMethod: isMeasurableEvent(taskType) ? MeasurementMethodOption.MANUAL : undefined,
        task: taskPayload
      })
    }
    // If this is a waste task, also create the STARTING event
    if (taskType === 'waste') {
      const startingEventPayload = {
        task: {
          id: payload.task.id,
          taskType: {
            id: payload.task.taskType?.id
          }
        },
        // carry over the currently selected remarks (or empty string)
        remarks: payload.remarks || '',
        status: EventStatus.STARTING,
        isFollowUp: false,
        ingredients: [],
        suggestions: []
      }
      const createdEvent = await actions.v1.performTask.createEvent(startingEventPayload)
      const newEventId = createdEvent?.id
      const newTaskId = createdEvent?.task?.id ?? payload.task.id // fallback to known-good id

      if (createdEvent && newEventId && newTaskId) {
        await actions.v1.performTask
          .getTask({
            taskId: createdEvent.task.id,
            siteId: state.site!.id,
            eventId: createdEvent.id || undefined
          })
          .then(taskDetail => {
            // set the payload to the new event's data
            setPayload({
              task: taskDetail!,
              choice: '',
              value: undefined,
              reportedActor: undefined,
              reportedSupplier: undefined,
              // For "Save and add new" we want a fresh form; don't carry over assets from task details.
              assets: [],
              remarks: taskDetail?.notes,
              suggestions: taskDetail?.suggestions,
              scheduledAt: undefined,
              incompleteToDo: undefined,
              ingredients: [],
              recordingDay: new Date().toISOString()
            })
            setCurrentEventId(createdEvent.id)
          })
        setResetTick(tick => tick + 1)
      }
      setLoading(false)
    }
  }

  const hasCurrentEvent = !!currentEventId

  return {
    payload,
    handleSubmit,
    handleSubmitAndAddNew,
    handleRemove,
    handleUpdate,
    handleCancel,
    setValue,
    loading,
    onAssetSaved,
    onAssetRemoved,
    handleNumberBlur,
    resetTick,
    hasCurrentEvent,
    currentEventId
  }
}
